import { Clear } from "@mui/icons-material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { LoadingButton } from "@mui/lab";
import { Autocomplete, CircularProgress, Grid, Stack } from "@mui/material";
import TextField from "@mui/material/TextField";
import { FileSection, FormDatePicker, FormTextField } from "components/atoms";
import { DetailTableTabs, ReportHeader } from "components/molecules";
import { FormikHelpers, useField, useFormik } from "formik";
import { DialogHelper, RoleHelper, TimeHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import { isEmpty } from "lodash";
import {
  IDownloadExcelRequest,
  IDownloadProductionPlanRequest,
  IGetAllProductionPlansRequest,
  IPaginationRequest,
  IProduct,
} from "model";
import moment from "moment";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  DownloadActions,
  PrintActions,
  ProductionPlanActions,
} from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { MasterDataApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import * as yup from "yup";
import {
  ExportPPDModal,
  ProductionPlanByManufacturingDate,
  ProductionPlanByProductLot,
} from "./components";
import ImportDocumentByPlan from "./components/ImportDocumentByPlan";

const validationSchema = yup.object().shape({});
const _fileId = "import-production-plan";

const ProductionPlanContext = createContext<IGetAllProductionPlansRequest>({
  productId: "",
});

export const useProductionPlanContext = () =>
  useContext<IGetAllProductionPlansRequest>(ProductionPlanContext);
const ProductionPlan = () => {
  const fileRef = React.useRef<any>();
  const [openExportPPDModal, setOpenExportPPDModal] = useState(false);
  const [openImportDocumentByPlan, setOpenImportDocumentByPlan] =
    useState(false);

  const [refresh, setRefresh] = useState(false);

  const [request, setRequest] = useState<IGetAllProductionPlansRequest>({
    productId: "",
    fromDate: TimeHelper.startOfWeek("MM-DD-YYYY"),
    toDate: TimeHelper.endOfWeek("MM-DD-YYYY"),
    pageSize: CURRENT_PAGE_SIZE,
    pageNumber: FIRST_PAGE,
  });
  const action = useAppSelector((state) => state.ProductionPlanReducer.action);
  const printAction = useAppSelector((state) => state.PrintReducer.action);
  const dispatch = useAppDispatch();

  const clearFilePath = () => {
    if (fileRef.current) {
      fileRef.current.value = "";
    }
  };

  const handleFileSelected = (files: FileList) => {
    if (files) {
      const file = files.item(0);
      DialogHelper.openConfirmImportDialog({
        title: "Import Production Plan Document",
        fileName: file?.name!,
        onConfirm: () =>
          dispatch(
            ProductionPlanActions.importProductionPlan.request({
              file: file as File,
            })
          ),
      });
    }
  };

  useEffect(() => {
    if (
      action.includes(ProductionPlanActions.importProductionPlan.successName) ||
      action.includes(ProductionPlanActions.importProductionPlan.failedName)
    ) {
      clearFilePath();
      dispatch(ProductionPlanActions.clearReducer.request());
      setRefresh(true);
    }
  }, [action]);

  useEffect(() => {
    if (printAction.includes(PrintActions.print.successName)) {
      dispatch(PrintActions.clearReducer.request());
      setRefresh(true);
    }
  }, [printAction]);

  const tabsName = [
    I18nTranslator(translations.receive.productionPlanByProductLot),
    I18nTranslator(translations.receive.productionPlanByManufacturingDate),
  ];

  const tabsPanel = [
    <ProductionPlanByProductLot request={request} />,
    <ProductionPlanByManufacturingDate request={request} />,
  ];
  return (
    <ProductionPlanContext.Provider value={request}>
      <FileSection
        ref={fileRef}
        id={_fileId}
        onFileSelected={handleFileSelected}
      />
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          height: "100%",
        }}
      >
        <DetailHeader
          request={request}
          onSubmit={setRequest}
          onImportDocumentByPlan={() => setOpenImportDocumentByPlan(true)}
        />
        <DetailTableTabs
          hideHeaderButton
          tabsName={tabsName}
          tabsPanel={tabsPanel}
        />
      </div>

      <ExportPPDModal
        open={openExportPPDModal}
        onClose={() => setOpenExportPPDModal(false)}
      />
      <ImportDocumentByPlan
        open={openImportDocumentByPlan}
        onClose={() => setOpenImportDocumentByPlan(false)}
      />
    </ProductionPlanContext.Provider>
  );
};

const CompanyAutoComplete = (props: {
  id: string;
  label: string;
  data?: IProduct[];
  loading: boolean;
}) => {
  const { id, label, data, loading } = props;
  const [_, meta, helper] = useField(id);

  const [open, setOpen] = React.useState(false);

  return (
    <Autocomplete
      id={id}
      sx={{ width: "100%" }}
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      isOptionEqualToValue={(option, value) =>
        option.productCode === value.productCode
      }
      getOptionLabel={(option) =>
        `${option.productCode} - ${option.description}`
      }
      options={data || []}
      loading={true}
      value={meta.value}
      onChange={(_, value) => {
        if (value) {
          helper.setValue(value);
        }
      }}
      clearIcon={
        <Clear
          onClick={() => {
            helper.setValue(null);
          }}
        />
      }
      renderInput={(params) => (
        <TextField
          {...params}
          name={id}
          label={label}
          fullWidth
          error={meta.touched && !isEmpty(meta.error)}
          helperText={meta.touched && meta.error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
    />
  );
};

interface IGetProductionPlanFormikRequest {
  product?: IProduct | null;
  productId: string;
  searchText: string;
  fromDate: moment.Moment;
  toDate: moment.Moment;
}

const DetailHeader = (props: {
  request: IGetAllProductionPlansRequest;
  onSubmit(values: IGetAllProductionPlansRequest): void;
  onImportDocumentByPlan(): void;
}) => {
  const dispatch = useDispatch();
  const { request, onSubmit, onImportDocumentByPlan } = props;
  const [productRequest] = useState({
    pageNumber: 1,
    pageSize: 100000,
  });
  const { loading, result } = usePaginationRequest<
    IPaginationRequest,
    IProduct[]
  >(productRequest, MasterDataApiService.getAllProducts);

  const params = useParams<{ name: string }>();
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );
  const action = useAppSelector((state) => state.ProductionPlanReducer.action);
  const currentWarehouse = useAppSelector(
    (state) => state.IdentityReducer.currentWarehouse
  );

  const formik = useFormik<IGetProductionPlanFormikRequest>({
    initialValues: {
      product: null,
      productId: "",
      searchText: "",
      fromDate: moment(request.fromDate, "MM-DD-YYYY"),
      toDate: moment(request.toDate, "MM-DD-YYYY"),
    },
    validationSchema,
    onSubmit: (
      values: IGetProductionPlanFormikRequest,
      { validateForm }: FormikHelpers<any>
    ) => {
      validateForm().then(() => {
        const request: IGetAllProductionPlansRequest = {
          productId: isEmpty(values.product) ? "" : values.product!.id,
          searchText: values.searchText,
          fromDate: TimeHelper.formatMomentToDate(
            values.fromDate,
            "MM-DD-YYYY"
          ),
          toDate: TimeHelper.formatMomentToDate(values.toDate, "MM-DD-YYYY"),
          pageNumber: FIRST_PAGE,
          pageSize: CURRENT_PAGE_SIZE,
        };
        onSubmit(request);
      });
    },
  });

  const onDownloadProductionPlanReport = async () => {
    const params: IDownloadProductionPlanRequest = {
      ...request,
      productId: request.productId,
      fromDate: TimeHelper.formatDate(
        request?.fromDate! as string,
        "YYYY-MM-DD"
      ),
      toDate: TimeHelper.formatDate(request?.toDate! as string, "YYYY-MM-DD"),
      fileName: EXPORT_FILE_NAME.productionPlan,
    };
    dispatch(DownloadActions.downloadProductionPlan.request(params));
  };

  const _downloadReceiveTemplate = () => {
    const params: IDownloadExcelRequest = {
      fileName: EXPORT_FILE_NAME.productionPlanForm,
    };
    dispatch(DownloadActions.downloadProductionPlanForm.request(params));
  };

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.productionPlan)}
      formik={formik}
      loading={false}
      description={params.name}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <LoadingButton
            startIcon={<UploadFileIcon />}
            loading={action.includes(
              ProductionPlanActions.importProductionPlan.requestName
            )}
            variant="contained"
            sx={{
              display:
                currentWarehouse?.id === "13f622cc-7f78-4f6b-a706-a177250dec02" ? "flex" : "none",
            }}
            onClick={onImportDocumentByPlan}
            disabled={
              !(
                RoleHelper.isSuperAdmin() ||
                RoleHelper.isAdmin() ||
                RoleHelper.isPlanner()
              )
            }
          >
            {I18nTranslator(translations.button.importDocsByPlan)}
          </LoadingButton>
          <label htmlFor={_fileId}>
            <LoadingButton
              loading={action.includes(
                ProductionPlanActions.importProductionPlan.requestName
              )}
              component={"span"}
              startIcon={<UploadFileIcon />}
              variant="contained"
              disabled={
                !(
                  RoleHelper.isSuperAdmin() ||
                  RoleHelper.isAdmin() ||
                  RoleHelper.isPlanner()
                )
              }
            >
              {I18nTranslator(translations.button.importDocs)}
            </LoadingButton>
          </label>
          <LoadingButton
            loading={downloading}
            startIcon={<DownloadIcon />}
            onClick={_downloadReceiveTemplate}
            variant="contained"
          >
            {I18nTranslator(translations.button.exportTemplate)}
          </LoadingButton>
          <LoadingButton
            loading={downloading}
            startIcon={<DownloadIcon />}
            onClick={onDownloadProductionPlanReport}
            variant="contained"
            disabled={
              !(
                RoleHelper.isSuperAdmin() ||
                RoleHelper.isAdmin() ||
                RoleHelper.isPlanner()
              )
            }
          >
            {I18nTranslator(translations.button.exportDocs)}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={2} item>
          <FormDatePicker
            id="fromDate"
            label={I18nTranslator(translations.fromDate)}
          />
        </Grid>
        <Grid xs={2} item>
          <FormDatePicker
            id="toDate"
            label={I18nTranslator(translations.toDate)}
          />
        </Grid>
        <Grid xs={8} item>
          <FormTextField
            label={I18nTranslator(translations.placeholder.search)}
            placeholder={I18nTranslator(
              translations.placeholder.searchProductionPlan
            )}
            id="searchText"
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

export default ProductionPlan;
